<template>
  <div>
    <main-header>
      <v-spacer />
      <v-btn
        v-for="item in buttons"
        :key="item.title + '-menu'"
        :loading="item.loading || false"
        color="primary"
        class="ma-2 hidden-md-and-down"
        dark
        @click="item.click"
      >
        {{ item.title }}
        <v-icon right>
          {{ item.icon }}
        </v-icon>
      </v-btn>
      <v-menu class="hidden-lg-and-up">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            color="primary"
            class="hidden-lg-and-up"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            v-for="item in buttons"
            :key="item.title + '-menu'"
            dense
          >
            <v-btn
              :loading="item.loading || false"
              color="primary"
              class="ma-2"
              width="100%"
              dark
              @click="item.click"
            >
              {{ item.title }}
              <v-icon right>
                {{ item.icon }}
              </v-icon>
            </v-btn>
          </v-list-item>
        </v-list>
      </v-menu>
    </main-header>
    <v-container fluid>
      <v-row class="text-center">
        <v-col cols="12">
          <EditRateModal
            v-model="editDialog"
            :rate="editedItem"
            @save="getList"
          />
          <CreateRateModal
            v-model="createDialog"
            @save="getList"
          />
          <UploadRateModal
            v-model="uploadDialog"
            @upload="getList"
          />
          <DeleteRateModal
            v-model="deleteModal"
            :rate="deletedItem"
            @delete="getList"
          />
          <ClearManualRatesModal
            v-model="clearManualRatesDialog"
            :rates="clearItems"
            @clear="getList"
          />
          <v-data-table
            :headers="headers"
            :items="displayedRates"
            class="elevation-1 table"
            multi-sort
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :custom-sort="keepServerOrder"
            :loading="loadingTable"
            hide-default-footer
            item-key="key"
            :items-per-page="perPage"
            :item-class="rowClass"
            fixed-header
            @update:sort-by="getListWithSort"
            @update:sort-desc="getListWithSort"
          >
            <template v-slot:top>
              <v-toolbar
                class="toolbar"
                height="84"
                flat
              >
                <div class="toolbar-content-special d-flex align-center w-100">
                  <SelectProject
                    v-if="tenantsList && tenantsList.length > 0"
                    :tenants-list="tenantsList"
                    @update="onChangedSelectedProject"
                  />
                  <v-btn
                    style="margin-left: 15px"
                    fab
                    small
                    color="orange"
                    :dark="hasCornerCases"
                    :disabled="!hasCornerCases"
                    :loading="isLoadingCornerCases"
                    @click="onClickCornerCases"
                  >
                    <v-icon :dark="hasCornerCases">
                      mdi-alert
                    </v-icon>
                  </v-btn>
                  <v-checkbox
                    v-model="showFirstTransaction"
                    label="Show 1st transaction"
                    hide-details
                    class="ml-4 mt-0 pt-0"
                    dense
                  />
                  <v-spacer />
                  <FilterInput
                    v-model="filters"
                    :items="searchArray"
                  />
                </div>
              </v-toolbar>

              <v-divider />
            </template>
            <template v-slot:item.marginDesired="{ item }">
              <MarginCell
                :margin-desired="item.marginDesired"
                :margin-desired-ranges="item.marginDesiredRanges"
              />
            </template>
            <template v-slot:item.marketRate="{ item }">
              {{ item.marketRate | formatMoney(4) }}
            </template>
            <template v-slot:item.sendingCurrencyUsdRate="{ item }">
              {{ item.sendingCurrencyUsdRate | formatMoney(4) }}
            </template>
            <template v-slot:item.usdReceiveCurrencyRate="{ item }">
              {{ item.usdReceiveCurrencyRate | formatMoney(4) }}
            </template>
            <template v-slot:item.providerRate="{ item }">
              <CalcEndUserCell
                :calc-end-user-rate="item.providerRate"
                :rate-ranges="item.providerRateRanges"
              />
            </template>
            <template v-slot:item.provider="{ item }">
              <ProviderCell
                v-if="item.provider"
                :provider="item.provider"
                :updated-at="item.providerRateUpdatedAt"
                :rate-ranges="item.providerRateRanges"
              />
            </template>
            <template v-slot:item.calcEndUserRate="{ item }">
              <CalcEndUserCell
                :calc-end-user-rate="item.calcEndUserRate"
                :calc-discounted-user-rate="item.calcDiscountedUserRate"
                :rate-ranges="item.automaticallyRate ? item.rateRanges : []"
                prefer-scalar
              />
            </template>
            <template v-slot:item.automaticallyRate="{ item }">
              <v-chip
                :color="
                  item.automaticallyRate ? 'blue lighten-4' : 'yellow lighten-3'
                "
              >
                {{ item.automaticallyRate ? "Automated" : "Manual" }}
              </v-chip>
            </template>
            <template v-slot:item.firstTransactionRateDiscount="{ item }">
              <span v-if="item.segment === 'FIRST_TRANSACTION'">—</span>
              <v-chip
                v-else-if="item.firstTransactionRateDiscount"
                color="amber lighten-1"
              >
                {{ item.firstTransactionRateDiscount }} %
              </v-chip>
            </template>
            <template v-slot:item.firstTransactionRateDiscountEnabled="{ item }">
              <span v-if="item.segment === 'FIRST_TRANSACTION'">—</span>
              <v-chip
                v-else
                color="amber lighten-1"
              >
                {{ item.firstTransactionRateDiscountEnabled ? "Enabled" : "Disabled" }}
              </v-chip>
            </template>
            <template v-slot:item.segment="{ item }">
              <v-chip
                :color="item.segment === 'FIRST_TRANSACTION' ? 'amber lighten-2' : 'blue lighten-4'"
                small
              >
                {{ formatSegment(item.segment) }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex">
                <v-icon
                  small
                  class="mr-2"
                  @click="() => onOpenArchive(item)"
                >
                  mdi-archive-clock
                </v-icon>
                <v-icon
                  small
                  class="mr-2"
                  :disabled="!isAdmin && !isEditor"
                  @click="() => editItem(item)"
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  small
                  class="mr-2"
                  :disabled="!isAdmin && !isEditor"
                  @click="() => deleteItem(item)"
                >
                  mdi-trash-can
                </v-icon>
              </div>
            </template>
            <template v-slot:no-data>
              <v-btn
                color="primary"
                dark
                v-on="getList"
              >
                Refresh
              </v-btn>
            </template>
            <template v-slot:footer>
              <v-container
                fluid
                class="mt-4"
              >
                <v-row>
                  <v-col cols="9">
                    <v-pagination
                      v-model="page"
                      class="b-pagination"
                      :length="pageLength"
                      :total-visible="11"
                      @input="onChangePage"
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-select
                      v-model="perPage"
                      class="select-per-page"
                      :items="perPageItems"
                      placeholder="Items per page"
                      @change="perPage = parseInt($event, 10)"
                    />
                  </v-col>
                </v-row>
              </v-container>
              <div class="text-center py-2" />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import MainHeader from "@/components/MainHeader";
import EditRateModal from "@/components/modals/EditRateModal";
import DeleteRateModal from "@/components/modals/DeleteRateModal";
import CreateRateModal from "@/components/modals/CreateRateModal";
import UploadRateModal from "@/components/modals/UploadRateModal";
import ClearManualRatesModal from "@/components/modals/ClearManualRatesModal";

import FilterInput from "@/components/FilterInput";
import SelectProject from "@/components/SelectProject";
import ProviderCell from "@/components/ProviderCell";
import CalcEndUserCell from "@/components/CalcEndUserCell";
import MarginCell from "@/components/MarginCell";

// utils
import {
  autopricingTableHeaders,
  autopricingFilters
} from "@/utils/staticTablesColumns";
import {
  openNewTabWithFilter
} from '@/utils/helpers';

export default {
  name: "Autopricing",
  components: {
    MainHeader,
    SelectProject,
    FilterInput,
    UploadRateModal,
    CreateRateModal,
    EditRateModal,
    DeleteRateModal,
    ClearManualRatesModal,
    ProviderCell,
    CalcEndUserCell,
    MarginCell
  },

  data() {
    return {
      restoreFromQuery: false,
      perPage: 10,
      sortBy: [],
      sortDesc: [],
      filters: [],
      buttons: [
        {
          title: "Download",
          icon: "mdi-cloud-download",
          click: this.csvExport,
          loading: false
        },
        {
          title: "Upload",
          icon: "mdi-upload",
          click: () => (this.uploadDialog = true),
          loading: false
        },
        {
          title: "New rate",
          icon: "mdi-plus",
          click: () => (this.createDialog = true),
          loading: false
        },
        {
          title: "Clear manual rate",
          icon: "mdi-broom",
          click: () => this.clearManualRates(),
          loading: false
        }
      ],
      headers: autopricingTableHeaders,
      loadingTable: false,
      editDialog: false,
      createDialog: false,
      uploadDialog: false,
      deleteModal: false,
      clearManualRatesDialog: false,
      editedItem: {},
      deletedItem: {},
      clearItems: [],
      page: 4,
      timerId: null,
      perPageItems: [10, 25, 50, 100, 500],
      hasCornerCases: false,
      isLoadingCornerCases: false,
      cornerCases: [],
      filtersInputList: autopricingFilters,
      showFirstTransaction: false
    };
  },

  computed: {
    isAdmin() {
      return this.$store.getters["AuthorizationStore/getUserIsAdmin"];
    },
    isEditor() {
      return this.$store.getters["AuthorizationStore/getUserIsEditor"];
    },
    searchArray() {
      return this.filtersInputList.filter(i => i.sortable !== false);
    },
    pageLength() {
      return this.$store.getters["RecordStore/getPagination"].totalPages || 0;
    },
    rates() {
      return this.$store.getters["RecordStore/getList"];
    },
    displayedRates() {
      if (!this.showFirstTransaction) return this.rates;
      const result = [];
      for (const row of this.rates) {
        result.push(row);
        if (row.firstTransactionRecord) {
          const ft = row.firstTransactionRecord;
          result.push({
            ...ft,
            key: `${ft.sendingCountryIso3}_${ft.receiveCountryIso3}_${ft.receiveCurrency}_${ft.serviceCode}_ft`,
            automaticallyRate: ft.manualRate === null,
            calcEndUserRate: ft.manualRate === null ? ft.calcEndUserRate : ft.manualRate
          });
        }
      }
      return result;
    },
    tenantsList() {
      return this.$store.getters["RecordStore/getTenants"];
    }
  },

  watch: {
    perPage(value) {
      if (this.restoreFromQuery) return;
      this.getListAndGoToFirstPage();
      this.addQueryToRoute("perPage", value);
      this.addQueryToRoute("page", 1);
    },
    filters: {
      handler() {
        if (this.restoreFromQuery) return;
        this.getListWithFilters();
      },
      deep: true
    },
    showFirstTransaction(value) {
      if (this.restoreFromQuery) return;
      this.addQueryToRoute("showFirstTransaction", value);
    },
  },

  beforeCreate() {
    const query = this.$route.query;
    if (query.tenant) {
      this.$store.dispatch("AppStore/setTenant", query.tenant);
    }
  },

  created() {
    if (!this.$store.getters["RecordStore/getTenants"]) {
      this.$store.dispatch("RecordStore/getTenants");
    }
    this.addQueryToRoute("tenant", this.$store.getters["AppStore/getTenant"]);
    if (this.$route.query) {
      this.restoreFromQuery = true;
      const query = this.$route.query;
      this.perPage = parseInt(query.perPage) || 10;
      this.page = parseInt(query.page) || 1;
      this.showFirstTransaction = query.showFirstTransaction === "true";
      let sort = query.sort || [];
      if (sort.length) {
        if (typeof sort === "string") sort = [sort];
        const sortBy = [];
        const sortDesc = [];
        sort.forEach(item => {
          const array = item.split(",");
          sortBy.push(array[0]);
          sortDesc.push(array[1] === "asc" ? false : true);
        });
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
      }
      let filters = query.filters || [];
      if (filters.length) {
        if (typeof filters === "string") filters = [filters];
        const filtersArray = [];
        filters.forEach(item => {
          const array = item.split(",");
          const filter = {};
          filter.key = array[0];
          filter.value = array[0] === 'cornerCases' ? array[1].split("|") : array[1];
          filter.name = array[2];
          filtersArray.push(filter);
        });
        this.filters = filtersArray;
      }
    }
    this.getList();
    this.$store.dispatch("PayoutMethodStore/getList");
    this.$store.dispatch("ActiveCountryStore/getList");
    this.getCornerCases();
  },

  methods: {
    rowClass(item) {
      return item.segment === 'FIRST_TRANSACTION' ? 'ft-row' : '';
    },
    formatSegment(segment) {
      if (!segment) return "Standard";
      return segment
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    onChangedSelectedProject() {
      this.addQueryToRoute("tenant", this.$store.getters["AppStore/getTenant"]);
      this.getListAndGoToFirstPage();
      this.getCornerCases();
    },
    onClickCornerCases() {
      if (this.hasCornerCases && this.cornerCases.length) {
        const origin = window.location.origin;
        const pathname = window.location.pathname;
        const url = origin + pathname + '?filters=cornerCases,' + this.cornerCases.join("|") + ',Corner cases';
        window.open(url, '_blank');
      }
    },
    onOpenArchive (data) {
      const pathname = '/audit-log-rates';
      const filter = `sendingCountryIso3:${ data.sendingCountryIso3 }:Send country,receiveCountryIso3:${ data.receiveCountryIso3 }:Recieve country,receiveCurrency:${ data.receiveCurrency }:Recieving currency,serviceCode:${ data.serviceCode }:Payout Method`;

      openNewTabWithFilter(pathname, filter);
    },
    getCornerCases() {
      this.isLoadingCornerCases = true;
      this.hasCornerCases = false;

      this.$store.dispatch("RecordStore/getCornerCases")
        .then((res) => {
          const data = res.data;
          const cornerCases = data.cornerCasesCorridorAmounts;
          const cornerCasesArray = [];
          let hasCornerCases = false;
          if (cornerCases) {
            for (let key in cornerCases) {
              if (cornerCases[key]) {
                hasCornerCases = true;
                cornerCasesArray.push(key);
              }
            }
          }
          this.filtersInputList.forEach((item, index) => {
            if (item.value === 'cornerCases') {
              const filter = item;
              filter.items = Object.keys(cornerCases);
              this.$set(this.filtersInputList, 14, filter);
            }
          })
          this.hasCornerCases = hasCornerCases;
          this.cornerCases = cornerCasesArray;
        })
        .finally(() => {
          this.isLoadingCornerCases = false;
        })
    },
    getListWithFilters() {
      this.page = 1;
      let filters = this.filters.map(val => {
        if (val.key === 'cornerCases') {
          return `${val.key},${val.value.join("|")},${val.name}`
        }
        return `${val.key},${val.value},${val.name}`;
      });
      this.getList();
      this.addQueryToRoute("filters", filters);
      this.addQueryToRoute("perPage", this.perPage);
      this.addQueryToRoute("page", 1);
    },
    keepServerOrder(items) {
      // Sorting is done server-side (see getListWithSort). Returning items untouched stops
      // Vuetify's client-side re-sort, which would otherwise detach each First Transaction row
      // from its Standard parent. The server order already nests FT rows under their parent.
      return items;
    },
    getListWithSort() {
      if (this.loadingTable) return;
      this.page = 1;
      let sort = this.sortBy.map((val, index) => {
        return `${val},${this.sortDesc[index] ? "desc" : "asc"}`;
      });
      this.getList();
      this.addQueryToRoute("page", 1);
      this.addQueryToRoute("sort", sort);
    },
    addQueryToRoute(name, value) {
      this.$router.replace(
        { query: Object.assign({ ...this.$route.query }, { [name]: value }) },
        () => {}
      );
    },
    clearManualRates() {
      const corridors = [];
      this.rates.forEach(item => {
        if (!item.automaticallyRate) {
          const corridor = {
            receiveCountryIso3: item.receiveCountryIso3,
            receiveCurrency: item.receiveCurrency,
            sendingCountryIso3: item.sendingCountryIso3,
            serviceCode: item.serviceCode
          };
          return corridors.push(corridor);
        }
      });
      this.clearItems = corridors;
      this.clearManualRatesDialog = true;
    },
    onChangePage() {
      this.getList();
      this.addQueryToRoute("page", this.page);
    },
    getListAndGoToFirstPage() {
      if (this.loadingTable) return;
      this.page = 1;
      this.getList();
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.editDialog = true;
    },
    deleteItem(item) {
      this.deletedItem = Object.assign({}, item);
      this.deleteModal = true;
    },
    csvExport() {
      this.$set(this.buttons[0], "loading", true);
      return this.$store
        .dispatch("RecordStore/downloadCsv", this.getFiltersQuery())
        .then(res => {
          // Use a Blob object URL instead of a data: URI. data: URIs are size-capped by
          // the browser (~2MB in Chrome) and silently truncate large downloads.
          const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `rates_${this.$store.getters["AppStore/getTenant"]}_${new Date().toISOString()}.csv`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.$nextTick().then(() =>
            this.$set(this.buttons[0], "loading", false)
          );
        })
        .catch(() => this.$set(this.buttons[0], "loading", false));
    },
    getFiltersQuery() {
      return this.filters.reduce((acc, val) => {
        acc[val.key] = val.value;
        return acc;
      }, {});
    },
    getList() {
      this.loadingTable = true;
      this.$store
        .dispatch("RecordStore/getList", {
          page: this.page,
          size: this.perPage,
          ...this.filters.reduce((acc, val) => {
            acc[val.key] = val.value;
            return acc;
          }, {}),
          sort: this.sortBy.map(
            (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
          )
        })
        .finally(() => {
          this.loadingTable = false;
          this.restoreFromQuery = false;
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import "~vuetify/src/styles/styles.sass";

.select-per-page {
  margin: 0;
  padding: 0;
  max-width: 140px;
  float: right;
}

.b-pagination {
  float: left;
}

.table {
  &::v-deep {
    font-size: 14px;

    th {
      box-shadow: none !important;
    }
    td:not(.v-data-table__mobile-row),
    th:not(.v-data-table__mobile-row) {
      border-bottom: 1px solid #f2f2f2 !important;
    }
  }
}

.table::v-deep .with-percent:not(:empty):after {
  content: "%";
}

.table::v-deep tr:not(.v-data-table__mobile-table-row) {
  $col-size: 80px;
  background: white;
  @each $col-number in 1, 2, 3, 4 {
    th:nth-child(#{$col-number}),
    td:nth-child(#{$col-number}) {
      position: sticky !important;
      z-index: 4;
      min-width: $col-size;
      max-width: $col-size;
      left: ($col-number - 1) * $col-size;
      border-color: rgba(0, 0, 0, 0.12) !important;
    }
  }
  th:nth-last-child(1),
  td:nth-last-child(1) {
    position: sticky !important;
    position: -webkit-sticky !important;
    z-index: 4;
    min-width: $col-size;
    max-width: $col-size;
    right: 0;
    background: inherit;
    border-color: rgba(0, 0, 0, 0.12) !important;
  }
}

.table::v-deep .ft-row td {
  background-color: #fff9c4 !important;
}

.table::v-deep .ft-row td:nth-child(1) {
  border-left: 3px solid #f9a825 !important;
  padding-left: 20px !important;
}

.toolbar {
  .v-toolbar--prominent .v-toolbar__content {
    align-content: center;
  }

  .toolbar-title {
    align-self: flex-start !important;
  }

  &::v-deep {
    .v-toolbar__content {
      padding-left: 0;
      padding-right: 0;
    }

    @media (max-width: map-get($grid-breakpoints, "md")) {
      .v-toolbar__content {
        flex-wrap: wrap;
        justify-content: space-between;
      }
    }
  }
}

.toolbar-content-special {
  flex-direction: row;
  align-items: flex-start;
  flex: 1;

  @media (max-width: map-get($grid-breakpoints, "md")) {
    flex: 1 1 100%;
    flex-direction: column;
    order: 1;
  }
}

@media (max-width: map-get($grid-breakpoints, "md")) {
  .toolbar {
    height: auto !important;

    .toolbar-content-special {
      flex-flow: row wrap !important;
    }

    .input-projects {
      margin-left: 15px;
      margin-bottom: 15px;
      margin-top: 15px !important;
    }

    &::v-deep {
      .v-toolbar__content {
        height: auto !important;
      }
    }
  }
}
</style>
